import styled, { StyledComponent } from "@emotion/styled";
import { color } from "@smartsheet/design-tokens-react";
import { RequiredThemeKeys } from "@smartsheet/sds-theme-internal";
import React from "react";
import { ttNormsFontFamily, ttNormsVarFontFamily } from "../../../fonts/fonts";
import { typeRampLodestarDefinitions } from "../constants/lodestarConstants";
import {
    ursaTypeRampDefinitions,
    lodestarToUrsaVariantMap,
    ursaSemanticTypeRampDefinitions,
} from "../constants/ursaConstants";
import { SystemTypes } from "../types";

type variantTypes =
    | keyof typeof ursaTypeRampDefinitions
    | keyof typeof typeRampLodestarDefinitions
    | keyof typeof ursaSemanticTypeRampDefinitions;

interface TypeRampStyleProps {
    themeMode: RequiredThemeKeys;
    variant: variantTypes;
    /**
     * If true, we'll try and select the right variant of the TT Norms font.
     * We don't need this for Inter since it's the default font and this logic should
     * happen somewhere globally in the application.
     */
    includeTTNormsProVarFont?: boolean;
    lineClamp?: number;
    ref?: React.RefCallback<HTMLElement> | null;
    system?: SystemTypes;
}

const isLodestarVariant = (variant: variantTypes) => variant in typeRampLodestarDefinitions;
const isUrsaSemantic = (variant: variantTypes) => variant in ursaSemanticTypeRampDefinitions;
const isUrsaSemanticHeading = (variant: variantTypes) => ["h1", "h2", "h3", "h4", "h5", "h6"].includes(variant);

const getFontStyles = (system: SystemTypes, variant: variantTypes, themeMode: RequiredThemeKeys) => {
    const isUrsa = system === "ursa" || themeMode.includes("ursa");
    const isLodeStar = system === "lodestar" && themeMode === "lodestar-light";
    //adding if blocks instead of conditionally destructuring off of ursaTypeRampDefinitions or typeRampLodestarDefinitions
    // so we can easily support additional systems/themes in the future.
    if (isUrsa) {
        const { fontFamily, fontWeight, fontSize, lineHeight } = isLodestarVariant(variant)
            ? lodestarToUrsaVariantMap[variant as keyof typeof typeRampLodestarDefinitions]
            : ursaTypeRampDefinitions[variant as keyof typeof ursaTypeRampDefinitions] ||
              ursaSemanticTypeRampDefinitions[variant as keyof typeof ursaSemanticTypeRampDefinitions];
        return `
        font-weight: ${fontWeight}; 
        font-size: ${fontSize}; 
        line-height: ${lineHeight};
        font-family: ${
            fontFamily === ttNormsFontFamily
                ? `${ttNormsFontFamily};
            @supports (font-variation-settings: normal){
                font-family: ${ttNormsVarFontFamily};
            }`
                : ``
        };`;
    } else if (isLodeStar) {
        const { fontFamily, fontWeight, fontSize, lineHeight } =
            typeRampLodestarDefinitions[variant as keyof typeof typeRampLodestarDefinitions];
        if (fontFamily === "TT Norms Pro")
            return `
            font: ${fontWeight} ${fontSize}/${lineHeight} ${ttNormsFontFamily};
            @supports (font-variation-settings: normal){
                font-family: ${ttNormsVarFontFamily};
            };
        `;
        return `font-weight: ${fontWeight}; font-size: ${fontSize}; line-height: ${lineHeight};`;
    }
    //handle additional systems/themes or custom themes
    //return ``;
};

const getColor = (
    system: SystemTypes,
    variant: keyof typeof typeRampLodestarDefinitions,
    themeMode: RequiredThemeKeys
) => {
    if (isUrsaSemantic(variant)) {
        if (isUrsaSemanticHeading(variant)) {
            return `color: ${color("color--text--neutral--strong")};`;
        }
        return `color: ${color("color--text--neutral--default")};`;
    }
    if (themeMode.includes("ursa") && isLodestarVariant(variant)) {
        return `color: ${typeRampLodestarDefinitions[variant].color};`;
    }
    return `${
        system === "lodestar" && themeMode === "lodestar-light"
            ? `color: ${typeRampLodestarDefinitions[variant].color};`
            : ``
    }`;
};
//eslint-disable-next-line
export const TypeRampRoot: StyledComponent<TypeRampStyleProps, TypeRampStyleProps, any> = styled("span")`
    ${({ system = "ursa", variant, themeMode }) => getFontStyles(system, variant, themeMode)}
    ${({ system = "ursa", variant, themeMode }) =>
        getColor(system, variant as keyof typeof typeRampLodestarDefinitions, themeMode)}
    ${({ lineClamp }) =>
        lineClamp !== undefined
            ? `
            overflow: hidden;
            ${
                // Because lineClamp tends to be buggy in certain use cases (such as multiple lines
                // of elided text) We will use the old school ellipsis method for single line
                // clamping.
                lineClamp === 1
                    ? `
                        white-space: nowrap;
                        text-overflow: ellipsis;
                    `
                    : `
                        display: -webkit-box;
                        -webkit-box-orient: vertical;
                        -webkit-line-clamp: ${lineClamp};
                        line-clamp: ${lineClamp};
                    `
            }
        `
            : ""}
    ${({ includeTTNormsProVarFont }) =>
        includeTTNormsProVarFont
            ? `
            font-family: ${ttNormsFontFamily};

            @supports (font-variation-settings: normal) {
                font-family: ${ttNormsVarFontFamily};
            }
        `
            : ""}
`;
